import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/helpers";
import { Plus, School, MapPin, Users } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { class_name: "", instructor_id: "", location: "", capacity: 10, dates: "", start_time: "09:00", end_time: "11:00", parking_instructions: "", items_to_bring: "" };

export default function Classrooms() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role === "admin" || user?.role === "office_staff";
  const [classrooms, setClassrooms] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [myStudents, setMyStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [enrollTarget, setEnrollTarget] = useState(null);
  const [enrollStudentId, setEnrollStudentId] = useState("");

  const load = useCallback(async () => {
    try {
      const promises = [api.get("/classrooms")];
      if (isStaff) promises.push(api.get("/instructors"));
      if (user?.role === "parent") promises.push(api.get("/students"));
      const results = await Promise.all(promises);
      setClassrooms(results[0].data);
      if (isStaff) setInstructors(results[1].data);
      if (user?.role === "parent") setMyStudents(results[1]?.data || []);
    } catch {
      toast.error("Could not load classrooms");
    } finally {
      setLoading(false);
    }
  }, [isStaff, user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!form.class_name || !form.instructor_id || !form.location || !form.dates) {
      toast.error("Please fill in all required fields");
      return;
    }
    const dates = form.dates.split(",").map((d) => d.trim()).filter(Boolean);
    try {
      await api.post("/classrooms", { ...form, capacity: parseInt(form.capacity, 10), dates });
      toast.success("Classroom session created");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create classroom");
    }
  };

  const submitEnroll = async () => {
    if (!enrollStudentId) {
      toast.error("Select a student to enroll");
      return;
    }
    try {
      await api.post(`/classrooms/${enrollTarget.id}/enroll`, { student_id: enrollStudentId });
      toast.success("Enrolled successfully");
      setEnrollTarget(null);
      setEnrollStudentId("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.code === "payment_hold") {
        toast.error(detail.message, { duration: 6000 });
      } else {
        toast.error(detail || "Could not enroll");
      }
    }
  };

  return (
    <div className="space-y-5" data-testid="classrooms-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Classroom sessions across all locations.</p>
        {isStaff && (
          <Button onClick={() => setOpen(true)} className="bg-[hsl(var(--role-accent))] hover:opacity-95" data-testid="add-classroom-button">
            <Plus className="h-4 w-4 mr-2" /> New Session
          </Button>
        )}
      </div>

      {loading ? (
        <Skeleton className="h-64 rounded-[var(--radius)]" />
      ) : classrooms.length === 0 ? (
        <div className="rounded-[var(--radius)] border bg-card p-12 text-center space-y-2" data-testid="classrooms-empty-state">
          <School className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No classroom sessions scheduled yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {classrooms.map((c) => (
            <div key={c.id} className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/classrooms/${c.id}`)} data-testid="classroom-card">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold font-heading">{c.class_name}</h3>
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{c.remaining_seats}/{c.capacity} seats</span>
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.location}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Instructor: {c.instructor_name}</p>
              <p className="text-xs text-muted-foreground">{(c.dates || []).map((d) => formatDate(d)).join(", ")}</p>
              <p className="text-xs text-muted-foreground">{c.start_time} - {c.end_time}</p>
              {user?.role === "parent" && c.remaining_seats > 0 && (
                <Button
                  size="sm"
                  className="w-full bg-[hsl(var(--role-accent))] hover:opacity-95"
                  onClick={(e) => { e.stopPropagation(); setEnrollTarget(c); }}
                  data-testid="classroom-enroll-button"
                >
                  Enroll Student
                </Button>
              )}
              {user?.role === "parent" && c.remaining_seats === 0 && (
                <p className="text-xs text-center text-destructive font-medium">Class Full</p>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="add-classroom-dialog">
          <DialogHeader>
            <DialogTitle>New Classroom Session</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Class name</Label>
              <Input value={form.class_name} onChange={(e) => setForm((f) => ({ ...f, class_name: e.target.value }))} data-testid="classroom-form-name-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Instructor</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.instructor_id} onChange={(e) => setForm((f) => ({ ...f, instructor_id: e.target.value }))} data-testid="classroom-form-instructor-select">
                <option value="">Select instructor</option>
                {instructors.map((i) => (
                  <option key={i.id} value={i.id}>{i.full_name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} data-testid="classroom-form-location-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Capacity</Label>
              <Input type="number" min={1} value={form.capacity} onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))} data-testid="classroom-form-capacity-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Start time</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} data-testid="classroom-form-start-time-input" />
            </div>
            <div className="space-y-1.5">
              <Label>End time</Label>
              <Input type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} data-testid="classroom-form-end-time-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Class dates (comma separated, YYYY-MM-DD)</Label>
              <Textarea placeholder="2025-01-10, 2025-01-17, 2025-01-24" value={form.dates} onChange={(e) => setForm((f) => ({ ...f, dates: e.target.value }))} data-testid="classroom-form-dates-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Parking instructions (optional)</Label>
              <Input placeholder="e.g. Park in Lot B, enter through the side door" value={form.parking_instructions} onChange={(e) => setForm((f) => ({ ...f, parking_instructions: e.target.value }))} data-testid="classroom-form-parking-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Items to bring (optional)</Label>
              <Input placeholder="e.g. Permit, pencil, notebook" value={form.items_to_bring} onChange={(e) => setForm((f) => ({ ...f, items_to_bring: e.target.value }))} data-testid="classroom-form-items-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} data-testid="classroom-form-submit-button">Create Session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!enrollTarget} onOpenChange={(v) => !v && setEnrollTarget(null)}>
        <DialogContent data-testid="enroll-classroom-dialog">
          <DialogHeader>
            <DialogTitle>Enroll in {enrollTarget?.class_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Student</Label>
            <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={enrollStudentId} onChange={(e) => setEnrollStudentId(e.target.value)} data-testid="enroll-classroom-student-select">
              <option value="">Select student</option>
              {myStudents.map((s) => (
                <option key={s.id} value={s.id}>{s.full_name}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button onClick={submitEnroll} data-testid="enroll-classroom-submit-button">Confirm Enrollment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
